import { filter, flow, sortBy } from "lodash/fp";

import type {
  ConnectedAccountProvider,
  ConnectedCalendarProvider,
} from "@app/common/auth/api";
import { CALENDAR_PROVIDERS_BY_ID } from "@app/common/constants/providers";
import ROUTES from "@app/common/routes";
import type {
  CalendarProviderId,
  SSOProviderId,
} from "@app/common/types/providerTypes";
import { withQueryParamsUrl } from "@app/common/utils";
import Config from "@app/config";

const filterAccountProviders = filter<ConnectedAccountProvider>(
  (provider): provider is ConnectedAccountProvider =>
    provider.providerType === "login" && provider.providerEmail != null
);
const filterCalendarProviders = filter<ConnectedCalendarProvider>({
  providerType: "calendar" as const,
});

export const isHighValueProvider = (
  providerId: CalendarProviderId | { providerId: CalendarProviderId }
) =>
  CALENDAR_PROVIDERS_BY_ID[
    typeof providerId === "string" ? providerId : providerId.providerId
  ]?.isHighValue ?? false;

const sortProviders = sortBy<
  ConnectedAccountProvider & ConnectedCalendarProvider
>("providerId");

export const getAccountProvidersFromData = flow<
  any,
  ConnectedAccountProvider[],
  ConnectedAccountProvider[]
>(filterAccountProviders, sortProviders);

export const getCalendarProvidersFromData = flow<
  any,
  ConnectedCalendarProvider[],
  ConnectedCalendarProvider[]
>(filterCalendarProviders, sortProviders);

const getReturnUrl = (
  provider: SSOProviderId | CalendarProviderId,
  success: boolean,
  returnPath?: string,
  searchParams?: URLSearchParams
) => {
  let path = returnPath ?? ROUTES.USER_SETTINGS_PROFILE;
  let origin =
    window.location.origin === "//"
      ? Config.FRONTEND_URL
      : window.location.origin;
  if (returnPath?.startsWith("http")) {
    const url = new URL(returnPath);
    path = url.pathname;
    origin = url.origin;
  }

  const returnPathWithParams = withQueryParamsUrl(
    path,
    { success, action: "add_sso", provider: provider },
    searchParams
  );

  return `${origin}${returnPathWithParams}`;
};

const normalizeQueryParams = (
  params: URLSearchParams | { [key: string]: string }
): URLSearchParams => {
  if (params instanceof URLSearchParams) {
    return params;
  }
  return new URLSearchParams(params);
};

export const getOauthUrl = (
  provider: SSOProviderId | CalendarProviderId,
  options?: {
    returnPath?: string;
    errorReturnPath?: string;
    queryParams?: URLSearchParams | { [key: string]: string };
    forceConsent?: boolean;
    authQueryParams?: URLSearchParams | { [key: string]: string };
  }
) => {
  let searchParams: URLSearchParams | undefined = undefined;
  if (options?.queryParams) {
    searchParams = normalizeQueryParams(options.queryParams);
  }
  let authQueryParams: URLSearchParams | undefined = undefined;
  if (options?.authQueryParams) {
    authQueryParams = normalizeQueryParams(options.authQueryParams);
  }

  const returnUrl = getReturnUrl(
    provider,
    true,
    options?.returnPath,
    searchParams
  );
  const errorUrl = getReturnUrl(
    provider,
    false,
    options?.errorReturnPath,
    searchParams
  );

  const forceConsent = options?.forceConsent ?? true;

  // We always want to force consent when adding a new sso account,
  // so we can ask for any additional permissions we may need
  return `${
    Config.auth[provider].AUTH_URL
  }?force_consent=${forceConsent}&return_url=${encodeURIComponent(
    returnUrl
  )}&error_url=${encodeURIComponent(errorUrl)}${
    authQueryParams ? `&${authQueryParams.toString()}` : ""
  }`;
};
